import type { AssessmentStateEntry } from "@pandas/assessment-engine";
import type { Message } from "ably";
import type { IBaseOrientation } from "@/components/phone-capture/capture-wizard/commons";

export type PeerMessageType = keyof PeerMessageTypes;
export type MessagePayload<T extends PeerMessageType> = PeerMessageTypes[T];

export type ClientType = "scanner-device" | "evaluated-device" | "webapp";

/**
 * The colour the evaluated device shows whenever the white calibration screen is not up. Pure
 * black gives the glass photo its contrast; the scanner's camera pays for the unlit OLED in
 * metering and focus — the Front still meters 2–4 EV darker than its white twin seconds later.
 */
export const PEER_SCREEN_BASE_COLOR = "#000000";

export type PeerMessageTypes = {
	"session-status-update": SessionStatusUpdate;
	"device-orientation": DeviceOrientationUpdate;
	"offer-status": OfferStatusUpdate;
	"capture-flow-update": CaptureFlowUpdateMessage;
	"capture-flow-ack": CaptureFlowAckMessage;
};

export type SessionMetadata =
	| { type: "assessment-state"; entries: AssessmentStateEntry[] }
	| { type: "redirection"; url: string };

export type SessionStatus =
	| "pairing-started"
	| "image-capture-started"
	| "image-capture-completed"
	| "functional-assessments-completed"
	| "start-functional-assessments";

export type SessionStatusUpdate = {
	client: ClientType;
	status: SessionStatus;
	metadata?: SessionMetadata;
};

export type DeviceOrientationUpdate = {
	orientation: IBaseOrientation;
	isMoving: boolean;
};

export type OfferStatus = "offer-presented" | "report-presented";

export type OfferStatusUpdate = {
	status: OfferStatus;
};

export type CaptureFlowUpdateMessage = {
	screenColor?: string;
	takingPhoto?: boolean;
};

export type CaptureFlowAckMessage = {
	screenColor: string;
	isScreenColorApplied: true;
	screenVisible?: boolean;
};

export type PeerMessage = Omit<Message, "name" | "data"> &
	{
		[K in PeerMessageType]: { name: K; data: MessagePayload<K> };
	}[PeerMessageType];
